// TournamentDetails.tsx
import React, { useEffect, useState } from "react";
import { observer } from "mobx-react-lite";
import {
  Button,
  Grid,
  Header,
  Icon,
  Image,
  Label,
  Message,
  Modal,
  Segment,
  Tab,
  TabPane,
} from "semantic-ui-react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useStore } from "../../app/stores/store";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import LoadingComponent from "../../app/layout/LoadingComponent";
import TournamentBracketView from "./TournamentBracketView";
import TournamentRoundRobinTable, {
  TournamentGroupStageView,
} from "./TournamentRoundRobinTable";
import TournamentRegistrationsPanel, {
  TournamentOrganizerPanel,
  TournamentRefereesPanel,
} from "./TournamentPanels";
import { TournamentChatPanel } from "./TournamentChatPanel";
import { RegisterToTournamentModal } from "./RegisterToTournamentModal";
import { TournamentDto } from "../../app/models/Tournament";
import { GameLocationPreview } from "../../components/map/GameLocationPreview";
import { useTournamentPoster } from "./useTournamentPoster";

// ── LifecycleActions ──────────────────────────────────────────────────────────
const LifecycleActions = observer(function LifecycleActions({
  t_,
  store,
}: {
  t_: TournamentDto;
  store: ReturnType<typeof useStore>["tournamentStore"];
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const canAdmin = t_.isCurrentUserOrganizer || t_.isCurrentUserCoOrganizer;
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [revertTarget, setRevertTarget] = useState<
    "draft" | "published" | "registrationOpen" | null
  >(null);

  if (!canAdmin) return null;

  const registeredCount =
    t_.registrations?.filter((r) => r.status === "Registered").length ?? 0;
  const hasRegisteredTeams = registeredCount > 0;
  const canDelete =
    [
      "Draft",
      "Published",
      "RegistrationOpen",
      "RegistrationClosed",
      "Cancelled",
    ].includes(t_.status) && t_.isCurrentUserOrganizer;

  const handleDeleteConfirm = async () => {
    const success = await store.deleteTournament(t_.id);
    if (success) navigate("/tournaments");
    setShowDeleteModal(false);
  };

  const handleCancelConfirm = async () => {
    await store.cancelTournament(t_.id);
    setShowCancelModal(false);
  };

  const handleRevertConfirm = async () => {
    if (!revertTarget) return;
    if (revertTarget === "draft") await store.revertToDraft(t_.id);
    if (revertTarget === "published") await store.revertToPublished(t_.id);
    if (revertTarget === "registrationOpen")
      await store.revertToRegistrationOpen(t_.id);
    setRevertTarget(null);
  };

  return (
    <>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 12 }}>
        {t_.status === "Draft" && (
          <Button
            positive
            size="small"
            loading={store.isSubmitting("lifecycle:publish")}
            disabled={store.submitting}
            onClick={() => store.publish(t_.id)}
          >
            <Icon name="send" />
            {t("tournament.actions.publish")}
          </Button>
        )}
        {t_.status === "Published" && (
          <Button
            color="green"
            size="small"
            loading={store.isSubmitting("lifecycle:openRegistration")}
            disabled={store.submitting}
            onClick={() => store.openRegistration(t_.id)}
          >
            <Icon name="unlock" />
            {t("tournament.actions.openRegistration")}
          </Button>
        )}
        {t_.status === "RegistrationOpen" && (
          <Button
            color="orange"
            size="small"
            loading={store.isSubmitting("lifecycle:closeRegistration")}
            disabled={store.submitting}
            onClick={() => store.closeRegistration(t_.id)}
          >
            <Icon name="lock" />
            {t("tournament.actions.closeRegistration")}
          </Button>
        )}
        {t_.status === "RegistrationClosed" && (
          <Button
            primary
            size="small"
            loading={store.generatingBracket}
            disabled={store.generatingBracket}
            onClick={() => store.generateBracket(t_.id)}
          >
            <Icon name="magic" />
            {t("tournament.actions.generateBracket")}
          </Button>
        )}
        {t_.status === "RegistrationClosed" && (
          <Button
            color="teal"
            size="small"
            loading={store.isSubmitting("lifecycle:start")}
            disabled={store.submitting || !store.bracketGenerated}
            onClick={() => store.startTournament(t_.id)}
          >
            <Icon name="play" />
            {t("tournament.actions.start")}
          </Button>
        )}
        {t_.status === "RegistrationClosed" && !store.bracketGenerated && (
          <Message warning size="small" style={{ marginTop: 8 }}>
            <Icon name="exclamation triangle" />
            {t("tournament.actions.generateBracketFirst")}
          </Message>
        )}
        {t_.status === "InProgress" && (
          <Button
            color="purple"
            size="small"
            loading={store.isSubmitting("lifecycle:complete")}
            disabled={store.submitting}
            onClick={() => store.completeTournament(t_.id)}
          >
            <Icon name="flag checkered" />
            {t("tournament.actions.complete")}
          </Button>
        )}
        {t_.status === "RegistrationClosed" && (
          <Button
            basic
            color="orange"
            size="small"
            loading={store.isSubmitting("lifecycle:revertToRegistrationOpen")}
            disabled={store.submitting}
            onClick={() => setRevertTarget("registrationOpen")}
          >
            <Icon name="undo" />
            {t("tournament.actions.revertToRegistrationOpen")}
          </Button>
        )}
        {t_.status === "RegistrationOpen" && (
          <Button
            basic
            color="grey"
            size="small"
            loading={store.isSubmitting("lifecycle:revertToPublished")}
            disabled={store.submitting}
            onClick={() => setRevertTarget("published")}
          >
            <Icon name="undo" />
            {t("tournament.actions.revertToPublished")}
          </Button>
        )}
        {t_.status === "Published" && (
          <Button
            basic
            color="grey"
            size="small"
            loading={store.isSubmitting("lifecycle:revertToDraft")}
            disabled={store.submitting}
            onClick={() => setRevertTarget("draft")}
          >
            <Icon name="undo" />
            {t("tournament.actions.revertToDraft")}
          </Button>
        )}
        {[
          "Draft",
          "Published",
          "RegistrationOpen",
          "RegistrationClosed",
        ].includes(t_.status) && (
          <Button
            negative
            basic
            size="small"
            loading={store.isSubmitting("lifecycle:cancel")}
            disabled={store.submitting}
            onClick={() => setShowCancelModal(true)}
          >
            <Icon name="ban" />
            {t("tournament.actions.cancel")}
          </Button>
        )}
        <Button basic size="small" as={Link} to={`/tournaments/${t_.id}/edit`}>
          <Icon name="edit" />
          {t("tournament.actions.edit")}
        </Button>
        {canDelete && (
          <Button
            basic
            negative
            size="small"
            onClick={() => setShowDeleteModal(true)}
          >
            <Icon name="trash" />
            {t("tournament.actions.delete")}
          </Button>
        )}
      </div>

      {/* Cancel modal */}
      <Modal
        size="mini"
        open={showCancelModal}
        onClose={() => setShowCancelModal(false)}
        dimmer="blurring"
      >
        <Modal.Header
          style={{
            backgroundColor: "#f8f8f9",
            borderBottom: "1px solid #d4d4d5",
          }}
        >
          <Icon name="ban" color="red" />
          {t("tournament.actions.cancelModalTitle")}
        </Modal.Header>
        <Modal.Content>
          {hasRegisteredTeams ? (
            <Message warning>
              <Icon name="exclamation triangle" />
              <Message.Content>
                <Message.Header>
                  {t("tournament.actions.cancelWarningTitle")}
                </Message.Header>
                <p>
                  {t("tournament.actions.cancelWarningMessage", {
                    count: registeredCount,
                  })}
                </p>
              </Message.Content>
            </Message>
          ) : (
            <p>{t("tournament.actions.cancelConfirmMessage")}</p>
          )}
        </Modal.Content>
        <Modal.Actions
          style={{
            backgroundColor: "#f9fafb",
            borderTop: "1px solid #d4d4d5",
            display: "flex",
            justifyContent: "flex-end",
            gap: 8,
          }}
        >
          <Button onClick={() => setShowCancelModal(false)}>
            {t("tournament.actions.cancelDelete")}
          </Button>
          <Button
            negative
            icon="ban"
            labelPosition="right"
            content={t("tournament.actions.cancelConfirmButton")}
            loading={store.isSubmitting("lifecycle:cancel")}
            disabled={store.submitting}
            onClick={handleCancelConfirm}
          />
        </Modal.Actions>
      </Modal>

      {/* Delete modal */}
      <Modal
        size="mini"
        open={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        dimmer="blurring"
      >
        <Modal.Header
          style={{
            backgroundColor: "#f8f8f9",
            borderBottom: "1px solid #d4d4d5",
          }}
        >
          <Icon name="trash" color="red" />
          {t("tournament.actions.deleteTitle")}
        </Modal.Header>
        <Modal.Content>
          {hasRegisteredTeams ? (
            <Message warning>
              <Icon name="exclamation triangle" />
              <Message.Content>
                <Message.Header>
                  {t("tournament.actions.deleteBlockedTitle")}
                </Message.Header>
                <p>
                  {t("tournament.actions.deleteBlockedMessage", {
                    count: registeredCount,
                  })}
                </p>
              </Message.Content>
            </Message>
          ) : (
            <p>{t("tournament.actions.deleteConfirmMessage")}</p>
          )}
        </Modal.Content>
        <Modal.Actions
          style={{
            backgroundColor: "#f9fafb",
            borderTop: "1px solid #d4d4d5",
            display: "flex",
            justifyContent: "flex-end",
            gap: 8,
          }}
        >
          <Button onClick={() => setShowDeleteModal(false)}>
            {t("tournament.actions.cancelDelete")}
          </Button>
          {!hasRegisteredTeams && (
            <Button
              negative
              icon="trash"
              labelPosition="right"
              content={t("tournament.actions.deleteConfirm")}
              loading={store.isSubmitting("lifecycle:delete")}
              disabled={store.submitting}
              onClick={handleDeleteConfirm}
            />
          )}
        </Modal.Actions>
      </Modal>

      {/* Revert confirmation modal */}
      <Modal
        size="mini"
        open={!!revertTarget}
        onClose={() => setRevertTarget(null)}
        dimmer="blurring"
      >
        <Modal.Header
          style={{
            backgroundColor: "#f8f8f9",
            borderBottom: "1px solid #d4d4d5",
          }}
        >
          <Icon name="undo" />
          {t("tournament.actions.revertModalTitle")}
        </Modal.Header>
        <Modal.Content>
          <p>{t("tournament.actions.revertConfirm")}</p>
        </Modal.Content>
        <Modal.Actions
          style={{
            backgroundColor: "#f9fafb",
            borderTop: "1px solid #d4d4d5",
            display: "flex",
            justifyContent: "flex-end",
            gap: 8,
          }}
        >
          <Button onClick={() => setRevertTarget(null)}>
            {t("tournament.actions.cancelDelete")}
          </Button>
          <Button
            primary
            icon="undo"
            labelPosition="right"
            content={t("tournament.actions.revertConfirmButton")}
            loading={store.submitting}
            disabled={store.submitting}
            onClick={handleRevertConfirm}
          />
        </Modal.Actions>
      </Modal>
    </>
  );
});

// ── TournamentDetails ─────────────────────────────────────────────────────────
export default observer(function TournamentDetails() {
  const { id } = useParams<{ id: string }>();
  const { tournamentStore } = useStore();
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState(0);
  const { generatePoster } = useTournamentPoster();

  // Sync initial tab from query param (e.g. ?tab=referees)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tab = params.get("tab");
    if (tab === "referees") setActiveTab(2);
    else if (tab === "admin") setActiveTab(5);
  }, []);

  useEffect(() => {
    if (id) {
      tournamentStore.loadTournament(id);
      tournamentStore.loadMatches(id);
      tournamentStore.loadStandings(id);
    }
    return () => tournamentStore.clearSelectedTournament();
  }, [id]);

  const t_ = tournamentStore.selectedTournament;

  if (tournamentStore.loadingInitial || !t_)
    return <LoadingComponent content={t("tournament.loading")} />;

  const statusColor: Record<string, string> = {
    Draft: "grey",
    Published: "blue",
    RegistrationOpen: "green",
    RegistrationClosed: "orange",
    InProgress: "teal",
    Completed: "purple",
    Cancelled: "red",
  };

  const formatIcon: Record<string, string> = {
    SingleElimination: "sitemap",
    DoubleElimination: "code branch",
    RoundRobin: "sync",
    GroupStageKnockout: "th",
  };

  const panes = [
    {
      menuItem: {
        key: "info",
        content: t("tournament.tabs.info"),
        icon: "info circle",
      },
      render: () => (
        <TabPane>
          <TournamentInfoPanel tournament={t_} />
        </TabPane>
      ),
    },
    {
      menuItem: {
        key: "registrations",
        content: t("tournament.tabs.teams"),
        icon: "users",
      },
      render: () => (
        <TabPane>
          <TournamentRegistrationsPanel tournament={t_} />
        </TabPane>
      ),
    },
    {
      menuItem: {
        key: "referees",
        content: t("tournament.tabs.referees"),
        icon: "gavel",
      },
      render: () => (
        <TabPane>
          <TournamentRefereesPanel tournament={t_} />
        </TabPane>
      ),
    },
    ...(t_.format === "RoundRobin"
      ? [
          {
            menuItem: {
              key: "standings",
              content: t("tournament.tabs.bracket"),
              icon: "list ol",
            },
            render: () => (
              <TabPane>
                <TournamentRoundRobinTable />
              </TabPane>
            ),
          },
        ]
      : []),
    ...(t_.format === "GroupStageKnockout"
      ? [
          {
            menuItem: {
              key: "groups",
              content: t("tournament.tabs.groups"),
              icon: "object group",
            },
            render: () => (
              <TabPane>
                <TournamentGroupStageView tournament={t_} />
              </TabPane>
            ),
          },
        ]
      : []),
    ...([
      "SingleElimination",
      "DoubleElimination",
      "GroupStageKnockout",
    ].includes(t_.format)
      ? [
          {
            menuItem: {
              key: "bracket",
              content: t("tournament.tabs.bracket"),
              icon: "sitemap",
            },
            render: () => (
              <TabPane>
                <TournamentBracketView tournament={t_} />
              </TabPane>
            ),
          },
        ]
      : []),
    {
      menuItem: {
        key: "chat",
        content: t("tournament.tabs.chat"),
        icon: "chat",
      },
      render: () => (
        <TabPane>
          <TournamentChatPanel
            contextType="Tournament"
            contextId={t_.id}
            tournament={t_}
          />
        </TabPane>
      ),
    },
    ...(t_.isCurrentUserOrganizer || t_.isCurrentUserCoOrganizer
      ? [
          {
            menuItem: {
              key: "admin",
              content: t("tournament.tabs.admin"),
              icon: "settings",
            },
            render: () => (
              <TabPane>
                <TournamentOrganizerPanel tournament={t_} />
              </TabPane>
            ),
          },
        ]
      : []),
  ];

  return (
    <Grid stackable>
      <Grid.Column width={16}>
        <Segment>
          <div className="tournament-header">
            <Image
              src={t_.logoUrl || "/assets/tournament-placeholder.png"}
              rounded
              className="tournament-logo"
            />
            <div className="tournament-info">
              <div className="tournament-title">
                <Header as="h2" style={{ margin: 0 }}>
                  {t_.name}
                </Header>
                <Label color={statusColor[t_.status] as any} size="small">
                  {t(`tournament.status.${t_.status.toLowerCase()}`)}
                </Label>
                <Label basic size="small">
                  <Icon name={formatIcon[t_.format] as any} />
                  {t(`tournament.format.${t_.format.toLowerCase()}`)}
                </Label>
                {t_.isOnline && (
                  <Label color="blue" basic size="small">
                    <Icon name="wifi" />
                    {t("tournament.online")}
                  </Label>
                )}
              </div>

              <div className="tournament-meta">
                <span>
                  <Icon name="futbol" />
                  {t(`sports.${t_.sportName.toLowerCase()}`) || t_.sportName}
                </span>
                <span>
                  <Icon name="user" />
                  <Link to={`/profiles/${t_.organizerId}`}>
                    {t_.organizerName}
                  </Link>
                </span>
                <span>
                  <Icon name="calendar" />
                  {format(new Date(t_.startsAt), "dd MMM yyyy")}
                </span>
                <span>
                  <Icon name="users" />
                  {t_.registeredTeamsCount} / {t_.maxTeams}{" "}
                  {t("tournament.teams")}
                </span>
                {t_.entryFee != null && (
                  <span>
                    <Icon name="ticket" />
                    {t_.entryFee === 0
                      ? t("tournament.free")
                      : `€${t_.entryFee}`}
                  </span>
                )}
                {t_.organizerPhone && (
                  <span>
                    <Icon name="phone" />
                    {t_.organizerPhone}
                  </span>
                )}
              </div>

              <LifecycleActions t_={t_} store={tournamentStore} />
            </div>

            <div className="tournament-actions">
              {t_.status === "RegistrationOpen" &&
                !t_.isCurrentUserParticipant && (
                  <RegisterToTournamentModal tournament={t_} />
                )}
              
              {(t_.isCurrentUserOrganizer || t_.isCurrentUserCoOrganizer) && (
                <Button
                  primary
                  size="small"
                  icon="file image"
                  labelPosition="left"
                  content={t("tournament.poster.generatePoster")}
                  className="poster-button"
                  onClick={() => generatePoster(t_)}
                />
              )}
              
              {t_.isCurrentUserCaptainOfRegisteredTeam &&
                t_.status === "RegistrationOpen" && (
                  <Label color="green" basic size="small">
                    <Icon name="check" />
                    {t("tournament.alreadyRegistered")}
                  </Label>
                )}
            </div>
          </div>
        </Segment>

        <Tab
          panes={panes}
          menu={{ secondary: true, pointing: true }}
          style={{ marginTop: 16 }}
          activeIndex={activeTab}
          onTabChange={(_, d) => setActiveTab(d.activeIndex as number)}
        />
      </Grid.Column>
    </Grid>
  );
});

// ── TournamentInfoPanel ───────────────────────────────────────────────────────
function TournamentInfoPanel({ tournament: t_ }: { tournament: any }) {
  const { t } = useTranslation();

  return (
    <div>
      <Segment secondary style={{ marginBottom: 16 }}>
        <div
          style={{
            display: "flex",
            gap: 24,
            flexWrap: "wrap",
            fontSize: "0.9em",
          }}
        >
          {[
            {
              icon: "unlock",
              label: t("tournament.info.regOpens"),
              val: t_.registrationOpensAt,
            },
            {
              icon: "lock",
              label: t("tournament.info.regCloses"),
              val: t_.registrationClosesAt,
            },
            {
              icon: "play",
              label: t("tournament.info.starts"),
              val: t_.startsAt,
            },
            {
              icon: "flag checkered",
              label: t("tournament.info.ends"),
              val: t_.endsAt,
            },
          ].map(({ icon, label, val }) =>
            val ? (
              <div key={label}>
                <div
                  style={{ color: "#999", fontSize: "0.8em", marginBottom: 2 }}
                >
                  {label}
                </div>
                <div style={{ fontWeight: 600 }}>
                  <Icon name={icon as any} color="teal" />
                  {format(new Date(val), "dd MMM yyyy, HH:mm")}
                </div>
              </div>
            ) : null,
          )}
        </div>
      </Segment>

      <div
        style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 16 }}
      >
        <Label basic size="small" color="orange">
          <Icon name="snowflake" />
          {t(`tournament.rosterFreeze.${t_.rosterFreezePolicy.toLowerCase()}`)}
          {t_.rosterFreezePolicy === "Flexible" &&
            ` (${t_.rosterFreezeDaysBeforeStart}d)`}
        </Label>
        <Label basic size="small" color="teal">
          <Icon
            name={
              t_.registrationMode === "AutoAccept" ? "bolt" : "clipboard list"
            }
          />
          {t(
            `tournament.registrationMode.${t_.registrationMode.toLowerCase()}`,
          )}
        </Label>
        {t_.disputeTimeoutHours && (
          <Label basic size="small">
            <Icon name="clock" />
            {t("tournament.info.disputeTimeout", {
              hours: t_.disputeTimeoutHours,
            })}
          </Label>
        )}
      </div>

      {t_.prizePool && (
        <Message color="yellow" icon>
          <Icon name="trophy" />
          <Message.Content>
            <Message.Header>{t("tournament.info.prizePool")}</Message.Header>
            {t_.prizePool}
          </Message.Content>
        </Message>
      )}

      {t_.description && (
        <Segment>
          <Header sub color="grey">
            {t("tournament.info.description")}
          </Header>
          <p style={{ whiteSpace: "pre-wrap" }}>{t_.description}</p>
        </Segment>
      )}

      {t_.rules && (
        <Segment>
          <Header sub color="grey">
            <Icon name="list" />
            {t("tournament.info.rules")}
          </Header>
          <p style={{ whiteSpace: "pre-wrap" }}>{t_.rules}</p>
        </Segment>
      )}

      {!t_.isOnline && t_.latitude && t_.longitude && (
        <Segment>
          <Header sub color="grey">
            <Icon name="map marker alternate" color="teal" />
            {t("tournament.info.location")}
          </Header>
          <Button
            size="small"
            color="teal"
            icon="external alternate"
            content={t("tournament.info.openInGoogleMaps")}
            onClick={() =>
              window.open(
                `https://www.google.com/maps?q=${t_.latitude},${t_.longitude}`,
                "_blank",
              )
            }
            style={{ marginBottom: 12 }}
          />
          <GameLocationPreview
            latitude={t_.latitude}
            longitude={t_.longitude}
          />
        </Segment>
      )}

      {t_.isOnline && (
        <Segment>
          <Header sub color="grey">
            <Icon name="wifi" color="blue" />
            {t("tournament.info.location")}
          </Header>
          <Label color="blue" basic>
            <Icon name="wifi" />
            {t("tournament.online")}
          </Label>
        </Segment>
      )}

      {t_.coOrganizers?.length > 0 && (
        <Segment>
          <Header sub color="grey">
            {t("tournament.info.coOrganizers")}
          </Header>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            {t_.coOrganizers.map((co: any) => (
              <Link key={co.userId} to={`/profiles/${co.userId}`}>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <Image
                    src={co.imageUrl || "/assets/user.png"}
                    avatar
                    style={{ width: 28 }}
                  />
                  <span style={{ fontSize: "0.88em" }}>{co.fullName}</span>
                </div>
              </Link>
            ))}
          </div>
        </Segment>
      )}
    </div>
  );
}
